// components/three/npc/NPCInstancedAnimationProvider.jsx
'use client'

import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useGLTF } from '@react-three/drei'
import { SkeletonUtils } from 'three-stdlib'
import * as THREE from 'three/webgpu'
import { storage, uniform, attribute, vec3 } from 'three/tsl'
import SkinningNode from 'three/src/nodes/accessors/SkinningNode.js'
import { instanceIndex } from 'three/src/nodes/core/IndexNode.js'
import { useFrame } from '@react-three/fiber'
import { IDLE_CLIP, SEATED_CLIPS, STAND_TO_SIT, SIT_TO_STAND } from '../../../lib/world/actionClips'
import { LID_CLOSED_ANGLE, LID_AXIS, BLINK_MS, blinkCurve, nextBlinkGap } from '../../../lib/world/blink'

const NPCInstancedAnimationContext = createContext(null)
const MAX_INSTANCES_DEFAULT = 64
const _releaseMatrix = new THREE.Matrix4()
const _unitScale = new THREE.Vector3(1, 1, 1)
// The axis the lids roll about, shared by every lid of every instance.
const _lidAxis = new THREE.Vector3(
  LID_AXIS === 'x' ? 1 : 0, LID_AXIS === 'y' ? 1 : 0, LID_AXIS === 'z' ? 1 : 0,
)
// Scratch for the per-frame roll. The lid sweeps a half turn, and slerping
// between two quaternions that far apart is the antipodal case: there is no
// shortest path and the result is arbitrary. Interpolating the ANGLE and
// building the rotation each frame sweeps predictably instead.
const _lidRoll = new THREE.Quaternion()
let _warnedNoLids = false
// lid.L / lidL / lid_L / LID.R, whatever the exporter and the loader
// between them decide to call it.
const LID_BONE = /^lid[._-]?[lr]$/i
// The lid geometry, named whatever the exporter left it as.
const LID_MESH = /eyelid/i

// Preload for faster first paint
useGLTF.preload('/models/bird.glb')

function findFirstSkinnedMesh(object) {
  let skinned = null
  object.traverse((child) => {
    if (child.isSkinnedMesh && !skinned) {
      skinned = child
    }
  })
  return skinned
}

// One rig per instance, carrying EVERY clip rather than one.
//
// Each instance already had its own skeleton and mixer, so this costs a few
// paused AnimationActions per slot and nothing per frame: only the clips
// with weight above zero are ever advanced.
function createRig(scene, clips) {
  if (!scene) return null
  const clone = SkeletonUtils.clone(scene)
  const skinned = findFirstSkinnedMesh(clone)
  if (!skinned) return null

  const mixer = new THREE.AnimationMixer(clone)
  const actions = new Map()

  for (const clip of clips) {
    const action = mixer.clipAction(clip)
    // The two transitions run once and hold their last frame; everything
    // else is a loop. A transition that looped would have him standing up
    // and sitting down forever.
    const oneShot = clip.name === STAND_TO_SIT || clip.name === SIT_TO_STAND
    action.loop = oneShot ? THREE.LoopOnce : THREE.LoopRepeat
    action.clampWhenFinished = oneShot
    action.enabled = true
    action.setEffectiveWeight(0)
    action.play()
    action.paused = true
    actions.set(clip.name, action)
  }

  // The lids, if this model has them. Their bind rotation is the open
  // pose, and shut is that turned about its own X: kept per rig because
  // each instance owns its own skeleton.
  // Bone names are matched loosely, and that is not laziness.
  //
  // The rig calls these lid.L and lid.R, but three.js sanitises node names
  // on load because a dot is a path separator in animation bindings, so
  // they arrive as lidL and lidR. (The body mesh shows the same thing:
  // "Retopo_Area (3)" comes through as "Retopo_Area_(3)".) Matching the
  // literal 'lid.L' found nothing, every frame, in silence, and cost two
  // unnecessary re-exports and most of a night before the warning below
  // finally got read.
  const lids = []
  for (const bone of skinned.skeleton.bones) {
    if (LID_BONE.test(bone.name)) lids.push({ bone, open: bone.quaternion.clone() })
  }

  if (lids.length === 0 && !_warnedNoLids) {
    _warnedNoLids = true
    console.warn('NPCInstancedAnimationProvider: no lid.L / lid.R bones in this model, so no blinking')
  }

  const first = actions.get(IDLE_CLIP) || actions.values().next().value || null
  if (first) {
    first.setEffectiveWeight(1)
    first.paused = false
  }

  return {
    root: clone,
    skeleton: skinned.skeleton,
    mixer,
    actions,
    // What is playing, what we are heading for, and the transition being
    // played on the way there (null most of the time).
    currentName: first ? (actions.get(IDLE_CLIP) ? IDLE_CLIP : [...actions.keys()][0]) : null,
    wantName: IDLE_CLIP,
    throughName: null,
    dirty: true,
    timeScale: 1,
    targetTimeScale: 1,
    lids,
    // Blink state. The first gap is randomised so a flock does not blink in
    // step the moment it spawns.
    lidBase: 0,
    lidWas: 0,
    blinkIn: nextBlinkGap(),
    blinkT: -1,
  }
}

/**
 * Point a rig at a clip, inserting a sit-down or stand-up on the way if the
 * body position is changing. Returns nothing; the frame loop does the work.
 */
function aimRig(rig, wantName) {
  if (!rig || !rig.actions.size) return
  const want = rig.actions.has(wantName) ? wantName : IDLE_CLIP
  if (rig.wantName === want) return
  rig.wantName = want

  const from = rig.currentName
  const seatedNow = SEATED_CLIPS.has(from)
  const seatedNext = SEATED_CLIPS.has(want)

  // Only the change of posture needs a transition. Sit to Sleep is already
  // seated, so it crossfades directly.
  if (!seatedNow && seatedNext && rig.actions.has(STAND_TO_SIT)) rig.throughName = STAND_TO_SIT
  else if (seatedNow && !seatedNext && rig.actions.has(SIT_TO_STAND)) rig.throughName = SIT_TO_STAND
  else rig.throughName = null
}

function createInstancedAnimationManager(scene, animations, maxInstances = MAX_INSTANCES_DEFAULT, clipName = 'BirdWalk') {
  if (!scene) return null

  const clone = SkeletonUtils.clone(scene)
  const skinnedMeshes = []
  clone.traverse((child) => {
    if (child.isSkinnedMesh) skinnedMeshes.push(child)
  })
  if (skinnedMeshes.length === 0) {
    console.warn('NPCInstancedAnimationProvider: no skinned mesh found in model')
    return null
  }

  const templateMesh = skinnedMeshes[0]
  const bonesPerInstance = templateMesh.skeleton.bones.length

  const boneMatricesArray = new Float32Array(maxInstances * bonesPerInstance * 16)
  const boneMatricesAttribute = new THREE.InstancedBufferAttribute(boneMatricesArray, 16)
  boneMatricesAttribute.setUsage(THREE.DynamicDrawUsage)

  // Initialize bone matrices with rest pose
  templateMesh.skeleton.update()
  const stride = bonesPerInstance * 16
  for (let i = 0; i < maxInstances; i++) {
    boneMatricesArray.set(templateMesh.skeleton.boneMatrices, i * stride)
  }
  boneMatricesAttribute.needsUpdate = true

  const bonesStorageNode = storage(boneMatricesAttribute, 'mat4', maxInstances * bonesPerInstance).toReadOnly()
  const bonesPerInstanceUniform = uniform(bonesPerInstance, 'uint')
  const instancedBoneMatricesNode = {
    element: (indexNode) => {
      const offset = instanceIndex.mul(bonesPerInstanceUniform)
      return bonesStorageNode.element(offset.add(indexNode))
    },
  }

  const instanceMatrix = new THREE.InstancedBufferAttribute(new Float32Array(maxInstances * 16), 16)
  instanceMatrix.setUsage(THREE.DynamicDrawUsage)
  const getMatrixTarget = new THREE.Matrix4()

  // Per-instance color storage (RGB as vec3)
  const instanceColorArray = new Float32Array(maxInstances * 3)
  // Initialize all to white
  for (let i = 0; i < maxInstances; i++) {
    instanceColorArray[i * 3 + 0] = 1.0
    instanceColorArray[i * 3 + 1] = 1.0
    instanceColorArray[i * 3 + 2] = 1.0
  }
  const instanceColorAttribute = new THREE.InstancedBufferAttribute(instanceColorArray, 3)
  instanceColorAttribute.setUsage(THREE.DynamicDrawUsage)

  // Create storage node for colors (similar to bone matrices)
  const colorsStorageNode = storage(instanceColorAttribute, 'vec3', maxInstances).toReadOnly()

  // Setup materials and skinning nodes for all meshes
  skinnedMeshes.forEach((mesh) => {
    mesh.castShadow = true
    mesh.receiveShadow = true
    mesh.frustumCulled = false

    mesh.material = new THREE.MeshStandardNodeMaterial({
      roughness: 0.45,
      metalness: 0.05,
    })

    const skinNode = new SkinningNode(mesh)
    skinNode.boneMatricesNode = instancedBoneMatricesNode

    mesh.material.positionNode = skinNode.getSkinnedPosition()
    mesh.material.normalNode = skinNode.getSkinnedNormal()

    // Show the model's baked vertex colours (COLOR_0). Previously this used a
    // per-instance storage colour, which came through black on the WebGPU
    // path — and it also discarded the bird's own plumage colours. Read the
    // vertex-colour attribute directly. (Per-instance tinting, if the ambient
    // flock returns, can multiply this by colorsStorageNode.element(...).)
    //
    // Except the eyelids, which carry no COLOR_0 at all: asking for a
    // vertex colour that is not there returns zero, so they closed over his
    // eyes as two black holes. White instead, which is also what lets the
    // dither pattern catch them.
    mesh.material.colorNode = LID_MESH.test(mesh.name)
      ? vec3(1, 1, 1)
      : attribute('color', 'vec3')

    // Convert to instanced
    mesh.isSkinnedMesh = false
    mesh.isInstancedMesh = true
    mesh.instanceMatrix = instanceMatrix
    mesh.raycast = THREE.InstancedMesh.prototype.raycast

    // Custom getMatrixAt for raycasting support
    mesh.getMatrixAt = (index, target = getMatrixTarget) => {
      target.fromArray(instanceMatrix.array, index * 16)
      return target
    }

    mesh.count = 0
  })

  const clips = animations || []
  if (clips.length === 0) console.warn('NPCInstancedAnimationProvider: no animation clips found in model')
  else if (!clips.some((c) => c.name === IDLE_CLIP)) {
    console.warn(`NPCInstancedAnimationProvider: no "${IDLE_CLIP}" clip; falling back to ${clips[0].name}`)
  }

  const rigs = Array.from({ length: maxInstances }, () => createRig(scene, clips))

  rigs.forEach((rig, index) => {
    if (!rig) return
    rig.root.updateMatrixWorld(true)
    rig.skeleton.update()
    boneMatricesArray.set(rig.skeleton.boneMatrices, index * stride)
  })
  boneMatricesAttribute.needsUpdate = true

  return {
    root: clone,
    meshes: skinnedMeshes,
    instanceMatrix,
    instanceColorAttribute,
    instanceColorArray,
    boneMatricesArray,
    boneMatricesAttribute,
    bonesPerInstance,
    rigs,
    maxInstances,
  }
}

export function NPCInstancedAnimationProvider({
  children,
  modelPath = '/models/bird.glb',
  maxInstances = MAX_INSTANCES_DEFAULT,
  clipName = 'BirdWalk',
  yOffset = 0,
}) {
  const { scene, animations } = useGLTF(modelPath)
  const manager = useMemo(
    () => createInstancedAnimationManager(scene, animations, maxInstances, clipName),
    [scene, animations, maxInstances, clipName],
  )
  // Mutable handle for lint-friendly mutation of internal buffers/rigs.
  const managerRef = useRef(null)

  useEffect(() => {
    managerRef.current = manager
  }, [manager])

  // Map NPC id -> slot
  const slotsRef = useRef(new Map())
  const freeSlotsRef = useRef([])
  const interpolationRef = useRef(new Map())
  const initializedRef = useRef(false)
  const capacityRef = useRef(maxInstances)
  

  const initSlots = useCallback(() => {
    const m = managerRef.current
    if (!m) return

    const capacityChanged = capacityRef.current !== maxInstances
    if (!initializedRef.current || capacityChanged) {
      initializedRef.current = true
      capacityRef.current = maxInstances

      // IMPORTANT: Do NOT clear existing allocations here.
      // On cold reload, NPCManager may allocate slots immediately after the provider mounts.
      // If we clear here, instances “disappear” until something re-triggers allocation.
      //
      // Instead, rebuild freeSlots based on currently allocated slots.
      const taken = new Set(slotsRef.current.values())
      const free = []
      for (let i = 0; i < maxInstances; i++) {
        if (!taken.has(i)) free.push(i)
      }
      freeSlotsRef.current = free
    }
  }, [maxInstances])

  useEffect(() => {
    // Ensure slot pool is ready before other components’ effects call allocateSlot
    initSlots()
  }, [manager, initSlots])

  const api = useMemo(
    () => ({
      manager,
      allocateSlot: (id) => {
        initSlots()
        if (slotsRef.current.has(id)) return slotsRef.current.get(id)
        const slot = freeSlotsRef.current.shift()
        if (slot !== undefined) {
          slotsRef.current.set(id, slot)
          interpolationRef.current.delete(id)
          return slot
        }
        console.warn('NPCInstancedAnimationProvider: No free slots')
        return -1
      },
      releaseSlot: (id) => {
        initSlots()
        const slot = slotsRef.current.get(id)
        if (slot === undefined) return false

        slotsRef.current.delete(id)
        interpolationRef.current.delete(id)
        freeSlotsRef.current.push(slot)

        const m = managerRef.current
        if (m) {
          // Zero-scale matrix to hide the instance (reuse shared matrix)
          _releaseMatrix.makeScale(0, 0, 0)
          _releaseMatrix.toArray(m.instanceMatrix.array, slot * 16)
          m.instanceMatrix.needsUpdate = true

          const rig = m.rigs[slot]
          if (rig) {
            rig.actions.forEach((a) => { a.setEffectiveWeight(0); a.paused = true })
            rig.currentName = IDLE_CLIP
            rig.wantName = IDLE_CLIP
            rig.throughName = null
          }
        }
        return true
      },
      // Does the loaded model actually carry this clip? Callers that want
      // to play an OPTIONAL one-shot (the head tilt, before its animation
      // exists in bird.glb) ask first, because aimRig's unknown-clip
      // fallback is Idle, and a bird dropping to idle mid-stride reads as
      // a glitch rather than a missing file.
      hasClip: (name) => animations.some((a) => a.name === name),
      // Where one of his bones is in the world, for something worn to ride it (the witch's hat, Halloween.jsx): his
      // instance's matrix times the bone's place in his rig, as the frame loop last left it. False when he has no
      // slot or the rig no such bone.
      boneWorld: (id, boneName, target) => {
        const slot = slotsRef.current.get(id)
        const m = managerRef.current
        const rig = slot === undefined || !m ? null : m.rigs[slot]
        if (!rig) return false
        rig.bonesByName ??= new Map(rig.skeleton.bones.map((b) => [b.name, b]))
        const bone = rig.bonesByName.get(boneName)
        if (!bone) return false
        target.fromArray(m.instanceMatrix.array, slot * 16).multiply(bone.matrixWorld)
        return true
      },
      // The same bone at rest, in his own space: what a worn thing is fitted against
      boneRest: (boneName, target) => {
        const m = managerRef.current
        const rig = m?.rigs?.[0]
        const bone = rig?.skeleton.bones.find((b) => b.name === boneName)
        if (!bone) return false
        target.copy(rig.skeleton.boneInverses[rig.skeleton.bones.indexOf(bone)]).invert()
        return true
      },
      setInstanceColor: (id, r, g, b) => {
        initSlots()
        const slot = slotsRef.current.get(id)
        const m = managerRef.current
        if (slot === undefined || !m) return

        m.instanceColorArray[slot * 3 + 0] = r
        m.instanceColorArray[slot * 3 + 1] = g
        m.instanceColorArray[slot * 3 + 2] = b
        m.instanceColorAttribute.needsUpdate = true
      },
      updateInstance: (id, position, rotation, clipName, timeScale, lidBase) => {
        initSlots()
        const slot = slotsRef.current.get(id)
        const m = managerRef.current
        if (slot === undefined || !m) return

        let state = interpolationRef.current.get(id)
        if (!state) {
          state = {
            currentPos: new THREE.Vector3(position.x, position.y + yOffset, position.z),
            targetPos: new THREE.Vector3(position.x, position.y + yOffset, position.z),
            currentRot: new THREE.Quaternion(rotation.x, rotation.y, rotation.z, rotation.w),
            targetRot: new THREE.Quaternion(rotation.x, rotation.y, rotation.z, rotation.w),
          }
          interpolationRef.current.set(id, state)

          // Snap immediately so it appears (reuse shared matrix)
          _releaseMatrix.compose(state.currentPos, state.currentRot, _unitScale)
          _releaseMatrix.toArray(m.instanceMatrix.array, slot * 16)
          m.instanceMatrix.needsUpdate = true
        } else {
          state.targetPos.set(position.x, position.y + yOffset, position.z)
          state.targetRot.set(rotation.x, rotation.y, rotation.z, rotation.w)
        }

        const rig = m.rigs[slot]
        if (rig) {
          // `clipName` used to be an isMoving flag: 1 played the walk, 0 faded
          // everything out and left him in the BIND POSE, which is why he
          // froze mid-stride whenever he stopped. It is a clip name now, and
          // there is always one playing.
          if (typeof clipName === 'string') aimRig(rig, clipName)
          if (typeof timeScale === 'number' && Number.isFinite(timeScale)) {
            rig.targetTimeScale = Math.max(0, timeScale)
          } else {
            rig.targetTimeScale = 1
          }
          if (typeof lidBase === 'number' && Number.isFinite(lidBase)) {
            rig.lidBase = Math.max(0, Math.min(1, lidBase))
          }
        }
      },
    }),
    [manager, yOffset, initSlots, animations],
  )

  // Reusable objects — allocated once, reused every frame (no GC pressure)
  const frameDummyMatrix = useRef(new THREE.Matrix4())
  const frameScale = useRef(new THREE.Vector3(1, 1, 1))
  const frameActiveCache = useRef([])
  const lastDrawCount = useRef(-1)

  useFrame((_, delta) => {
    const m = managerRef.current
    if (!m) return

    const { rigs, boneMatricesArray, boneMatricesAttribute, bonesPerInstance, instanceMatrix } = m
    const stride = bonesPerInstance * 16

    let needsBonesUpload = false
    let needsMatrixUpload = false

    const safeDelta = Math.min(delta, 0.1)
    const lerpFactor = 1.0 - Math.exp(-15 * safeDelta)

    // Update draw count — only write to meshes when it actually changes
    let maxSlot = -1
    slotsRef.current.forEach((slot) => {
      if (slot > maxSlot) maxSlot = slot
    })
    const targetCount = maxSlot + 1
    if (lastDrawCount.current !== targetCount) {
      m.meshes.forEach((mesh) => { mesh.count = targetCount })
      lastDrawCount.current = targetCount
    }

    // Rebuild active list only when slot count changes (avoids Array.from every frame)
    const slots = slotsRef.current
    if (frameActiveCache.current.length !== slots.size) {
      frameActiveCache.current = Array.from(slots.entries())
    } else {
      let idx = 0
      slots.forEach((slot, id) => {
        const cached = frameActiveCache.current[idx]
        if (!cached || cached[0] !== id || cached[1] !== slot) {
          frameActiveCache.current[idx] = [id, slot]
        }
        idx++
      })
    }

    const active = frameActiveCache.current
    const dummy = frameDummyMatrix.current
    const scale = frameScale.current

    for (let i = 0; i < active.length; i++) {
      const [id, slot] = active[i]
      const rig = rigs[slot]
      if (!rig || !rig.actions?.size) continue

      // Transform interpolation
      const s = interpolationRef.current.get(id)
      if (s) {
        s.currentPos.lerp(s.targetPos, lerpFactor)
        s.currentRot.slerp(s.targetRot, lerpFactor)
        dummy.compose(s.currentPos, s.currentRot, scale)
        dummy.toArray(instanceMatrix.array, slot * 16)
        needsMatrixUpload = true
      }

      // Which clip he should be on THIS frame. A transition, if one is
      // pending, takes precedence over the destination until it has run.
      let playing = rig.throughName || rig.wantName
      if (rig.throughName) {
        const t = rig.actions.get(rig.throughName)
        // A one-shot that has reached its end has done its job. Comparing
        // against the clip duration rather than listening for the mixer's
        // finished event keeps this loop free of per-rig listeners.
        if (t && t.time >= t.getClip().duration - 1e-3) {
          rig.throughName = null
          playing = rig.wantName
        }
      }

      // Crossfade: the one that should be playing comes up, everything else
      // goes down. Fading rather than switching is what stops a visible pop
      // between, say, walking and sitting.
      const fade = safeDelta * 4.0
      let anyAudible = false
      rig.actions.forEach((act, name) => {
        const want = name === playing ? 1 : 0
        const w = act.getEffectiveWeight()
        let next = w
        if (w < want) next = Math.min(want, w + fade)
        else if (w > want) next = Math.max(want, w - fade)
        if (next !== w) act.setEffectiveWeight(next)

        if (next > 0.001) {
          anyAudible = true
          if (act.paused) act.paused = false
          // A clip coming back in starts from the top rather than resuming
          // wherever it was last left. Without this a returning clip picks
          // up mid-cycle, so a short action shows a fragment: an eat that
          // happened to pause at 90% would play its last tenth and stop.
          if (w <= 0.001) act.time = 0
        } else if (!act.paused && next <= 0.001) {
          act.paused = true
        }
      })
      rig.currentName = playing

      // Per-instance timeScale smoothing
      const tsDiff = rig.timeScale - rig.targetTimeScale
      if (tsDiff > 0.0001 || tsDiff < -0.0001) {
        const tsStep = safeDelta * 6.0
        if (rig.timeScale < rig.targetTimeScale) rig.timeScale = Math.min(rig.targetTimeScale, rig.timeScale + tsStep)
        else rig.timeScale = Math.max(rig.targetTimeScale, rig.timeScale - tsStep)
      } else {
        rig.timeScale = rig.targetTimeScale
      }
      rig.actions.forEach((act) => { act.timeScale = rig.timeScale })

      if (anyAudible) {
        rig.mixer.update(safeDelta)
        rig.dirty = true
      }

      // THE LIDS, after the mixer and before the skeleton is baked.
      //
      // This ordering is the whole trick. The clips all key the lids at a
      // constant open pose (Blender writes every armature bone into every
      // action), so writing them before the mixer would achieve nothing.
      // Written here, the clip has had its say and this lands on top, which
      // is also why Sleep not closing his eyes costs nothing.
      if (rig.lids.length) {
        const ms = safeDelta * 1000
        if (rig.blinkT >= 0) {
          rig.blinkT += ms
          if (rig.blinkT >= BLINK_MS) {
            rig.blinkT = -1
            rig.blinkIn = nextBlinkGap()
          }
        } else {
          rig.blinkIn -= ms
          // No blinking while his eyes are already shut: a bird asleep does
          // not blink, he just has his eyes closed.
          if (rig.blinkIn <= 0 && rig.lidBase < 0.9) rig.blinkT = 0
          else if (rig.blinkIn <= 0) rig.blinkIn = nextBlinkGap()
        }

        // Whichever is more shut: the resting position or the blink passing
        // through it. Taking the max rather than adding means a droopy bird
        // still blinks properly instead of overshooting.
        const closed = Math.max(rig.lidBase, rig.blinkT >= 0 ? blinkCurve(rig.blinkT) : 0)
        for (const lid of rig.lids) {
          _lidRoll.setFromAxisAngle(_lidAxis, LID_CLOSED_ANGLE * closed)
          lid.bone.quaternion.copy(lid.open).multiply(_lidRoll)
        }
        if (closed > 0.001 || rig.lidWas > 0.001) rig.dirty = true
        rig.lidWas = closed
      }

      if (rig.dirty) {
        rig.root.updateMatrixWorld(true)
        rig.skeleton.update()
        boneMatricesArray.set(rig.skeleton.boneMatrices, slot * stride)
        rig.dirty = false
        needsBonesUpload = true
      }
    }

    if (needsBonesUpload) boneMatricesAttribute.needsUpdate = true
    if (needsMatrixUpload) instanceMatrix.needsUpdate = true
  })

  if (!manager) return null

  return (
    <NPCInstancedAnimationContext.Provider value={api}>
      <primitive object={manager.root} />
      {children}
    </NPCInstancedAnimationContext.Provider>
  )
}

export const useNPCInstancedAnimations = () => useContext(NPCInstancedAnimationContext)


